import React, { useState } from 'react';
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  InputBase,
  Slider,
  Stack,
  Typography,
  useTheme,
} from '@mui/material';
import CheckIcon from '@mui/icons-material/Check';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import { useSnackbar } from 'notistack';
import useServerStateContext from '@/shared/hooks/useServerStateContext';
import Tracking from '@/shared/services/tracking';
import { aimAt, groundPointAt } from '../geometry';
import { HIGH_TARGET_FT, MAX_TARGET_HEIGHT, framingTarget } from '../store/framingTargetStore';
import { useTargetAiming } from '../hooks/useTargetAiming';
import { useCreateFramingPresetMutation, useFramingPresetsQuery } from '../hooks/useFramingPresets';

type StepState = 'todo' | 'current' | 'done';

const StepMarker = ({ n, state }: { n: number; state: StepState }) => {
  const { cx } = useTheme().palette;
  return (
    <Box
      aria-hidden="true"
      sx={{
        width: 18,
        height: 18,
        flexShrink: 0,
        borderRadius: '50%',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        fontSize: '11px',
        lineHeight: 1,
        ...(state === 'current' && { backgroundColor: cx.blue.main, color: cx.text.primary }),
        ...(state === 'done' && { backgroundColor: cx.bg.elevated2, color: cx.green }),
        ...(state === 'todo' && { border: `1px solid ${cx.border.default}`, color: cx.text.muted }),
      }}
    >
      {state === 'done' ? <CheckIcon sx={{ fontSize: 12 }} /> : n}
    </Box>
  );
};

const Step = ({
  n,
  state,
  title,
  children,
}: {
  n: number;
  state: StepState;
  title: string;
  children?: React.ReactNode;
}) => {
  const { cx } = useTheme().palette;
  return (
    <Stack direction="row" spacing={1} aria-current={state === 'current' ? 'step' : undefined}>
      <StepMarker n={n} state={state} />
      <Stack spacing={0.5} sx={{ flex: 1, minWidth: 0 }}>
        <Typography
          sx={{
            fontSize: '12px',
            lineHeight: '18px',
            color: state === 'todo' ? cx.text.muted : cx.text.primary,
          }}
        >
          {title}
        </Typography>
        {children}
      </Stack>
    </Stack>
  );
};

/**
 * Target mode, in place of the joystick under the player: walks through
 * aiming a Fixed Camera Clip at a spot (click the rink, raise it, save it as
 * a preset or apply it) and closes when done. A preset is a spot only; the
 * clip keeps its own zoom.
 */
export const TargetModePanel = ({ sx }: { sx?: object }) => {
  const { cx } = useTheme().palette;
  const { enqueueSnackbar } = useSnackbar();
  const { selectedCamera } = useServerStateContext();
  const { session, target, preset, released, applied, unreachable, saving } = useTargetAiming();
  const presetsQuery = useFramingPresetsQuery(selectedCamera?.id);
  const createPreset = useCreateFramingPresetMutation();
  const [name, setName] = useState('');
  const [nameError, setNameError] = useState<string | null>(null);

  if (!session) return null;

  const presets = presetsQuery.data ?? [];
  // One save at a time: a later apply could otherwise land before an earlier one.
  const busy = saving || createPreset.isPending;
  const placed = !!target;
  const raised = !!target && target.z > 0.05;
  // Out of reach up high is the zoom's doing; down on the ice it's the spot.
  const highTarget = !!target && target.z > HIGH_TARGET_FT;
  const step1: StepState = placed ? 'done' : 'current';
  // Raising is optional: naming the preset (or starting from one) moves on.
  const step2: StepState = !placed ? 'todo' : raised || preset || name.trim() ? 'done' : 'current';
  const step3: StepState = step2 === 'done' ? 'current' : 'todo';

  const helperSx = { fontSize: '11px', lineHeight: '15px', color: cx.text.tertiary };
  const linkSx = {
    minWidth: 0,
    p: 0,
    fontSize: '11px',
    lineHeight: '15px',
    textTransform: 'none',
    color: cx.blue.hover,
    verticalAlign: 'baseline',
    '&:hover': { backgroundColor: 'transparent', textDecoration: 'underline' },
  } as const;
  const primarySx = {
    minWidth: 0,
    height: 28,
    px: 1.5,
    fontSize: '12px',
    textTransform: 'none',
    whiteSpace: 'nowrap',
    color: cx.text.primary,
    backgroundColor: cx.blue.main,
    '&:hover': { backgroundColor: cx.blue.hover },
    '&.Mui-disabled': { backgroundColor: cx.bg.elevated2, color: cx.text.muted },
  } as const;
  const secondarySx = {
    minWidth: 0,
    height: 28,
    px: 1,
    fontSize: '12px',
    textTransform: 'none',
    color: cx.text.secondary,
  } as const;

  // The clip is aimed from the spot; the zoom stays what the preview shows.
  // It reads as framed once the save went through, if still on this spot.
  const frameClip = async (
    aim: { pan: number; tilt: number },
    framingPresetId: number | undefined,
    label: string | null,
  ) => {
    const spot = framingTarget.get().target;
    try {
      const saved = await framingTarget.applyTo(
        session,
        { ...aim, zoom: session.readCurrentPTZ().zoom },
        framingPresetId,
      );
      if (saved && framingTarget.isCurrent(session, spot)) framingTarget.markApplied(label);
    } catch (error: any) {
      enqueueSnackbar(error.message, { variant: 'error' });
    }
  };

  const applyPreset = () => {
    if (!preset || !target || !selectedCamera) return;
    Tracking.getInstance().track('Apply Framing Preset', {
      category: 'Clips',
      source: 'Target Mode',
    });
    const aim = presets.find((p) => p.id === preset.id)?.aim ?? aimAt(selectedCamera, target);
    frameClip(aim, preset.id, preset.name);
  };

  const applyWithoutSaving = () => {
    if (!target || !selectedCamera) return;
    frameClip(aimAt(selectedCamera, target), undefined, null);
  };

  const saveAndApply = async () => {
    const trimmed = name.trim();
    if (busy || !trimmed || !target || !selectedCamera) return;
    setNameError(null);
    try {
      const saved = await createPreset.mutateAsync({
        cameraId: selectedCamera.id,
        name: trimmed,
        target,
      });
      Tracking.getInstance().track('Save Framing Preset', {
        category: 'Clips',
        source: 'Target Mode',
      });
      setName('');
      // Back, another clip or a moved spot meanwhile: keep the preset, leave the clip.
      if (!framingTarget.isCurrent(session, target)) return;
      framingTarget.loadPreset(saved);
      frameClip(saved.aim, saved.id, saved.name);
    } catch (error: any) {
      const message = error?.response?.data?.message || error.message;
      if (error?.response?.status === 409) setNameError(message);
      else enqueueSnackbar(message, { variant: 'error' });
    }
  };

  // Starts from what the preview already shows: the ice at the centre of the frame.
  const startFromFrameCentre = () => {
    if (!selectedCamera) return;
    try {
      const spot = groundPointAt(selectedCamera, session.readCurrentPTZ());
      if (spot) framingTarget.place(spot.x, spot.y);
      else enqueueSnackbar('Tilt the camera down to the ice first', { variant: 'info' });
    } catch (error: any) {
      enqueueSnackbar(error.message, { variant: 'error' });
    }
  };

  const body = applied ? (
    <Stack spacing={1} role="status" sx={{ py: 0.5 }}>
      <Stack direction="row" spacing={1} sx={{ alignItems: 'center' }}>
        <CheckCircleIcon sx={{ fontSize: 18, color: cx.green }} />
        <Typography sx={{ fontSize: '12px', lineHeight: '18px', color: cx.text.primary }}>
          {applied.name ? `Clip framed on “${applied.name}”` : 'Clip position set'}
        </Typography>
      </Stack>
      <Typography sx={{ ...helperSx, pl: 3.25 }}>
        The clip aims at this spot and keeps its own zoom. Export it when you are ready.
      </Typography>
      <Stack direction="row" spacing={1} sx={{ pl: 3.25, pt: 0.5 }}>
        <Button onClick={() => framingTarget.end()} sx={primarySx}>
          Done
        </Button>
        <Button onClick={() => framingTarget.restart()} sx={secondarySx}>
          Aim somewhere else
        </Button>
      </Stack>
    </Stack>
  ) : (
    <Stack spacing={1.25}>
      <Step n={1} state={step1} title="Click the rink where the camera should aim">
        {placed && unreachable && !highTarget ? (
          <Typography sx={{ ...helperSx, color: cx.gold }}>
            {selectedCamera?.displayName ?? 'This camera'} can't frame this spot. Try one further
            from the camera, or another camera.
          </Typography>
        ) : placed ? (
          <Typography sx={helperSx}>
            {preset ? `From preset “${preset.name}”. ` : ''}Click the rink again to move it.
          </Typography>
        ) : released ? (
          <Typography sx={{ ...helperSx, color: cx.gold }}>
            You steered the camera, so it let go of the spot. Click the rink to aim again.
          </Typography>
        ) : (
          <Typography sx={helperSx}>
            Every camera turns to the same spot. Or{' '}
            <Button onClick={startFromFrameCentre} sx={linkSx}>
              use the centre of this frame
            </Button>
            .
          </Typography>
        )}
        {presets.length > 0 && (
          <Stack direction="row" sx={{ flexWrap: 'wrap', alignItems: 'center', gap: 0.5 }}>
            <Typography sx={{ ...helperSx, mr: 0.5 }}>Saved presets:</Typography>
            {presets.map((p) => (
              <Chip
                key={p.uniqueId}
                size="small"
                label={p.name}
                variant={preset?.id === p.id ? 'filled' : 'outlined'}
                onClick={() => framingTarget.loadPreset(p)}
                sx={{ height: 20, fontSize: '11px' }}
              />
            ))}
          </Stack>
        )}
      </Step>

      <Step n={2} state={step2} title="Raise it if needed">
        {placed && (
          <>
            <Stack direction="row" spacing={1.5} sx={{ alignItems: 'center', pr: 0.5 }}>
              <Slider
                size="small"
                aria-label="Target height"
                min={0}
                max={MAX_TARGET_HEIGHT}
                step={0.5}
                value={target.z}
                onChange={(_, v) => framingTarget.setHeight(v as number)}
                getAriaValueText={(v) => `${v} feet above the ice`}
                sx={{ color: cx.gold, flex: 1, ml: 0.75 }}
              />
              <Typography
                sx={{ fontSize: '12px', lineHeight: '16px', color: cx.text.primary, width: 40 }}
              >
                {target.z.toFixed(0)} ft
              </Typography>
            </Stack>
            {unreachable && highTarget ? (
              <Typography sx={{ ...helperSx, color: cx.gold }}>
                {selectedCamera?.displayName ?? 'This camera'} can't frame it this high at this
                zoom. Zoom in on the video, or lower it.
              </Typography>
            ) : (
              <Typography sx={helperSx}>
                {raised
                  ? `${target.z.toFixed(0)} ft above the ice.`
                  : 'For things above the ice, like the scoreboard. Drag the dot on the video or the slider.'}
              </Typography>
            )}
          </>
        )}
      </Step>

      <Step n={3} state={step3} title="Save it as a preset, or apply it to this clip">
        {placed &&
          (preset ? (
            <Stack direction="row" spacing={1} sx={{ alignItems: 'center' }}>
              <Button onClick={applyPreset} disabled={busy || !selectedCamera} sx={primarySx}>
                Apply “{preset.name}”
              </Button>
              <Typography sx={helperSx}>Move the spot to save a new one.</Typography>
            </Stack>
          ) : (
            <>
              <Stack direction="row" spacing={1}>
                <InputBase
                  value={name}
                  onChange={(e) => {
                    setName(e.target.value);
                    setNameError(null);
                  }}
                  onKeyDown={(e) => e.key === 'Enter' && saveAndApply()}
                  placeholder="Preset name, e.g. Scoreboard"
                  error={!!nameError}
                  inputProps={{
                    maxLength: 100,
                    'aria-label': 'Preset name',
                    'aria-invalid': !!nameError,
                  }}
                  sx={{
                    flex: 1,
                    height: 28,
                    px: 1,
                    fontSize: '12px',
                    color: cx.text.primary,
                    border: `1px solid ${nameError ? cx.red.main : cx.border.default}`,
                    borderRadius: 1,
                  }}
                />
                <Button
                  onClick={saveAndApply}
                  disabled={busy || !name.trim() || !selectedCamera}
                  sx={primarySx}
                >
                  {busy ? <CircularProgress size={12} /> : 'Save & apply'}
                </Button>
              </Stack>
              {nameError ? (
                <Typography role="alert" sx={{ ...helperSx, color: cx.red.main }}>
                  {nameError}
                </Typography>
              ) : (
                <Typography sx={helperSx}>
                  Presets work from every camera. Or{' '}
                  <Button
                    onClick={applyWithoutSaving}
                    disabled={busy || !selectedCamera}
                    sx={linkSx}
                  >
                    apply without saving
                  </Button>
                  .
                </Typography>
              )}
            </>
          ))}
      </Step>
    </Stack>
  );

  return (
    <Stack
      data-testid="target-mode-panel"
      spacing={1.25}
      sx={{
        p: 1,
        backgroundColor: cx.bg.surface3,
        borderRadius: 1,
        height: '100%',
        boxSizing: 'border-box',
        overflowX: 'hidden',
        overflowY: 'auto',
        ...sx,
      }}
    >
      {body}
    </Stack>
  );
};

export default TargetModePanel;
